import { useEffect, useState } from "react";
import StatCard from "../components/StatCard";
import CustomBarChart from "../components/CustomBarChart";
import CustomLineChart from "../components/CustomLineChart";
// import SplashScreen from "../components/SplashScreen";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import MapView from "../components/MapView";
import MapDash from "../components/MapDash";
import { useTranslation } from "react-i18next";

const Hotels = () => {
  const [data, setData] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [selectedHotelName, setSelectedHotelName] = useState("");
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [mapData, setMapData] = useState([]);
  const { t } = useTranslation();

  // لما المستخدم يغيّر اسم الفندق من القائمة
  const handleHotelChange = (e) => {
    const name = e.target.value.trim();
    setSelectedHotelName(name); // نخزن الاسم الجديد
  };

  // لما اسم الفندق يتغير، نحدث البيانات
  useEffect(() => {
    if (selectedHotelName) {
      const hotelsWithSameName = filteredHotels.filter(
        (h) => h.name.trim() === selectedHotelName
      );
      const merged = getMergedHotelData(hotelsWithSameName);
      setSelectedHotel(merged);
    } else {
      setSelectedHotel(null);
    }
  }, [selectedHotelName, filteredHotels]);

  const navigate = useNavigate();

  useEffect(() => {
    const localData = localStorage.getItem("hotelFormData");
    setMapData(localData ? JSON.parse(localData) : []);
    if (localData) {
      const parsedData = JSON.parse(localData);
      setData(parsedData);
      const locations = [...new Set(parsedData.map((hotel) => hotel.location))];
      setSelectedLocation(locations[0] || "");
    }
  }, []);

  useEffect(() => {
    if (!data) return;

    if (selectedLocation === "") {
      setFilteredHotels(data);
      setSelectedHotel(data[0]);
    } else {
      const filtered = data.filter(
        (hotel) => hotel.location === selectedLocation
      );
      setFilteredHotels(filtered);
      setSelectedHotel(filtered[0]);
    }
  }, [selectedLocation, data]);

  const locations = [...new Set(data?.map((hotel) => hotel.location))];

  const defaultHotel = {
    name: t("unknown"),
    numberOfRooms: 0,
    numberOfFlights: 0,
    numberOfVisitors: 0,
    hotelCategory: t("unavailable"),
    solarPowerCapacity: 0,
    solarWaterHeater: false,
    desalinationPlant: false,
    treatmentPlant: false,
    wasteSeparation: false,
    electricityBills: {},
    occupancyRates: {},
    location: t("unknown"),
  };

  const hotel = selectedHotel || defaultHotel;

  const electricityData = Object.entries(hotel.electricityBills || {}).map(
    ([year, bill]) => ({
      year,
      electricity: parseInt(bill, 10),
    })
  );

  const accommodationData = Object.entries(hotel.occupancyRates || {}).map(
    ([year, rate]) => ({
      year,
      percentage: parseInt(rate, 10),
    })
  );

  // if (!data || !selectedHotel) {
  //   return <SplashScreen />;
  // }
  // دالة منفصلة داخل نفس المكون
  const getMergedHotelData = (hotels) => {
    if (!hotels || hotels.length === 0) return defaultHotel;

    return hotels.reduce(
      (acc, curr) => {
        acc.name = curr.name;
        acc.location = curr.location;
        acc.hotelCategory = curr.hotelCategory;
        acc.numberOfRooms += parseInt(curr.numberOfRooms || 0, 10);
        acc.numberOfVisitors += parseInt(curr.numberOfVisitors || 0, 10);
        acc.numberOfFlights += parseInt(curr.numberOfFlights || 0, 10);
        acc.solarPowerCapacity += parseFloat(curr.solarPowerCapacity || 0);

        acc.solarWaterHeater = acc.solarWaterHeater || curr.solarWaterHeater;
        acc.desalinationPlant = acc.desalinationPlant || curr.desalinationPlant;
        acc.treatmentPlant = acc.treatmentPlant || curr.treatmentPlant;
        acc.wasteSeparation = acc.wasteSeparation || curr.wasteSeparation;

        for (const year in curr.electricityBills) {
          acc.electricityBills[year] =
            (acc.electricityBills[year] || 0) +
            parseInt(curr.electricityBills[year] || 0, 10);
        }

        for (const year in curr.occupancyRates) {
          acc.occupancyRates[year] =
            (acc.occupancyRates[year] || 0) +
            parseInt(curr.occupancyRates[year] || 0, 10);
        }

        return acc;
      },
      { ...defaultHotel }
    );
  };

  return (
    <>
      <Helmet>
        <title>{t("hotels")} | {t("dashboard")}</title>
      </Helmet>

      <div className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden">
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("hotels")}
          </h1>

          {/* فلتر القطاع والفندق */}
          <div className="flex flex-col sm:flex-row gap-2 w-full justify-between items-center">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-2/3">
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="border p-2 rounded w-full sm:w-1/2 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              >
                <option value="" disabled hidden>
                  {t("all_sectors")}
                </option>
                {locations.map((location, index) => (
                  <option key={index} value={location}>
                    {location}
                  </option>
                ))}
              </select>

              <select
                value={selectedHotelName}
                onChange={(e) => setSelectedHotelName(e.target.value.trim())}
                className="border p-2 rounded w-full sm:w-1/2 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              >
                <option value="">{t("select_hotel")}</option>
                {[...new Set(filteredHotels.map((hotel) => hotel.name))].map(
                  (name, index) => (
                    <option key={index} value={name}>
                      {name}
                    </option>
                  )
                )}
              </select>
            </div>

            <button
              onClick={() => navigate("/HotelsForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold mt-2 sm:mt-0"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>

        {/* بيانات الفندق */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          <div className="col-span-1 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 flex flex-col gap-4" dir="ltr">
            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">{t("hotel_info")}</h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard title={t("hotel_name")} value={hotel.name} />
                <StatCard title={t("number_of_rooms")} value={hotel.numberOfRooms} />
                <StatCard title={t("number_of_visitors")} value={hotel.numberOfVisitors} />
                <StatCard title={t("hotel_rating")} value={hotel.hotelCategory} />
              </div>
            </div>

            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">
                {t("electricity_consumed")}
              </h2>
              <CustomBarChart
                data={electricityData}
                xKey="year"
                barKey="electricity"
                barColor="#f59e0b"
                title={t("electricity_consumed")}
              />
            </div>

            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">{t("occupancy_rate")}</h2>
              <CustomLineChart
                data={accommodationData}
                xKey="year"
                yKeys={[{ name: "percentage", color: "#3b82f6" }]}
                title={t("occupancy_rate_years")}
              />
            </div>

            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">{t("environmental_characteristics")}</h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  title={t("solar_power_capacity")}
                  value={hotel.solarPowerCapacity}
                />
                <StatCard
                  title={t("solar_water_heater")}
                  value={hotel.solarWaterHeater ? t("yes") : t("no")}
                />
                <StatCard
                  title={t("desalination_plant")}
                  value={hotel.desalinationPlant ? t("yes") : t("no")}
                />
                <StatCard
                  title={t("treatment_plant")}
                  value={hotel.treatmentPlant ? t("yes") : t("no")}
                />
                <StatCard
                  title={t("waste_separation")}
                  value={hotel.wasteSeparation ? t("yes") : t("no")}
                />
              </div>
            </div>
          </div>

          <div className="md:col-span-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20">
            <MapDash initialData={mapData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Hotels;


